import React, { useState, useEffect } from 'react';

const SchoolInquiriesTab = () => {
    // --- STATE ---
    const [inquiries, setInquiries] = useState([]);
    const [filteredInquiries, setFilteredInquiries] = useState([]);
    const [selectedInquiry, setSelectedInquiry] = useState(null);
    const [loading, setLoading] = useState(false);

    // Filters
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [search, setSearch] = useState('');

    // --- INITIAL FETCH ---
    useEffect(() => {
        fetchInquiries();
    }, []);

    // --- FILTER LOGIC ---
    useEffect(() => {
        let temp = inquiries;

        // 1. Search Filter
        if (search) {
            const lowerSearch = search.toLowerCase();
            temp = temp.filter(item => 
                item.school_name?.toLowerCase().includes(lowerSearch) ||
                item.city?.toLowerCase().includes(lowerSearch) ||
                item.contact_person?.toLowerCase().includes(lowerSearch) ||
                item.phone?.includes(search)
            );
        }

        // 2. Date Filter
        if (startDate || endDate) {
            temp = temp.filter(item => {
                const itemDate = new Date(item.submitted_at).setHours(0,0,0,0);
                const start = startDate ? new Date(startDate).setHours(0,0,0,0) : null;
                const end = endDate ? new Date(endDate).setHours(23,59,59,999) : null;

                if (start && end) return itemDate >= start && itemDate <= end;
                if (start) return itemDate >= start;
                if (end) return itemDate <= end;
                return true;
            });
        }

        setFilteredInquiries(temp);
    }, [search, startDate, endDate, inquiries]);

    const fetchInquiries = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/admin/school-inquiries');
            const data = await res.json();
            if (Array.isArray(data)) {
                setInquiries(data);
                setFilteredInquiries(data);
            } else {
                setInquiries([]);
            }
        } catch (error) {
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- EXCEL DOWNLOAD HANDLER (CLIENT SIDE) ---
    const handleDownloadExcel = () => {
        if (filteredInquiries.length === 0) return alert("No data to export");

        // Headers
        const headers = [
            "Date", "School Name", "City", "Contact Person", "Phone", "Email",
            "Role Required", "Custom Role", "Level", "Subject", "Salary", "Vacancy Count", "Qualifications"
        ];

        // Rows Generation (Flattening Data)
        let csvRows = [];

        filteredInquiries.forEach(school => {
            const date = new Date(school.submitted_at).toLocaleDateString();
            const clean = (txt) => txt ? `"${txt.toString().replace(/"/g, '""')}"` : "";

            if (school.requirements && school.requirements.length > 0) {
                // If school has requirements, create a row for EACH requirement
                school.requirements.forEach(req => {
                    csvRows.push([
                        date,
                        clean(school.school_name),
                        clean(school.city),
                        clean(school.contact_person),
                        clean(school.phone),
                        clean(school.email),
                        clean(req.role),
                        clean(req.custom_role),
                        clean(req.level),
                        clean(req.subject),
                        clean(req.salary),
                        req.vacancy_count || 1,
                        clean(req.qualifications)
                    ].join(","));
                });
            } else {
                // If school has NO requirements listed, still show the school row
                csvRows.push([
                    date,
                    clean(school.school_name),
                    clean(school.city),
                    clean(school.contact_person),
                    clean(school.phone),
                    clean(school.email),
                    "No Requirements Listed", "", "", "", "", "", ""
                ].join(","));
            }
        });

        // Create Blob and Download
        const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...csvRows].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `School_Inquiries_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="space-y-6 animate-fade-in">
            
            {/* --- HEADER & FILTERS --- */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <div>
                        <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight">School Inquiries</h3>
                        <p className="text-xs text-slate-400 font-bold mt-1">
                            Total: {filteredInquiries.length} Schools
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-3 items-end">
                        {/* Date Filters */}
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">From Date</label>
                            <input 
                                type="date" 
                                value={startDate} 
                                onChange={(e) => setStartDate(e.target.value)} 
                                className="px-3 py-2 border rounded-lg text-xs font-bold text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">To Date</label>
                            <input 
                                type="date" 
                                value={endDate} 
                                onChange={(e) => setEndDate(e.target.value)} 
                                className="px-3 py-2 border rounded-lg text-xs font-bold text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Search */}
                        <div>
                             <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Search</label>
                            <input 
                                type="text" 
                                placeholder="Name, City..." 
                                value={search}
                                onChange={(e) => setSearch(e.target.value)} 
                                className="px-3 py-2 border rounded-lg text-xs w-32 md:w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Export Button */}
                        <button 
                            onClick={handleDownloadExcel} 
                            className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-4 py-2.5 rounded-lg flex items-center gap-2 shadow-md transition-all h-[38px]"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                            Export Excel
                        </button>
                        
                        {/* Clear Filter Button */}
                        {(startDate || endDate || search) && (
                            <button 
                                onClick={() => { setStartDate(''); setEndDate(''); setSearch(''); }}
                                className="text-red-500 text-xs font-bold underline px-2 h-[38px]"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>
                
                {/* --- TABLE --- */}
                <div className="overflow-x-auto border rounded-xl">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-bold">
                            <tr>
                                <th className="p-4 border-b">Date</th>
                                <th className="p-4 border-b">School Info</th>
                                <th className="p-4 border-b">Contact</th>
                                <th className="p-4 border-b">Requirements</th>
                                <th className="p-4 border-b text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm text-slate-700 divide-y divide-slate-100">
                            {filteredInquiries.length > 0 ? (
                                filteredInquiries.map((inq) => (
                                    <tr key={inq.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="p-4 text-slate-400 font-mono text-xs whitespace-nowrap">
                                            {new Date(inq.submitted_at).toLocaleDateString()}
                                        </td>
                                        <td className="p-4">
                                            <div className="font-bold text-slate-800">{inq.school_name}</div>
                                            <div className="text-xs font-medium text-slate-500">{inq.city}</div>
                                        </td>
                                        <td className="p-4">
                                            <div className="font-medium">{inq.contact_person}</div>
                                            <div className="text-xs text-blue-500 hover:underline cursor-pointer">{inq.phone}</div>
                                        </td>
                                        <td className="p-4">
                                            <span className={`px-2 py-1 rounded text-xs font-bold ${inq.req_count > 0 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-400'}`}>
                                                {inq.req_count} Roles Open
                                            </span>
                                        </td>
                                        <td className="p-4 text-center">
                                            <button 
                                                onClick={() => setSelectedInquiry(inq)} 
                                                className="bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1 rounded text-xs font-bold transition-colors"
                                            >
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-slate-400 font-medium">
                                        {loading ? "Loading..." : "No inquiries found within this date range."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- MODAL FOR DETAILS --- */}
            {selectedInquiry && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto transform transition-all scale-100">
                        {/* Modal Header */}
                        <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
                            <div>
                                <h2 className="text-2xl font-black text-slate-800">{selectedInquiry.school_name}</h2>
                                <p className="text-sm text-slate-500">{selectedInquiry.city}</p>
                            </div>
                            <button onClick={() => setSelectedInquiry(null)} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center font-bold text-slate-500 transition-colors">✕</button>
                        </div>

                        <div className="p-6 space-y-8">
                            {/* Contact Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 p-5 rounded-xl border border-slate-100">
                                <div><p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Contact Person</p><p className="font-bold text-slate-800">{selectedInquiry.contact_person}</p></div>
                                <div><p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Phone</p><p className="font-bold text-slate-800">{selectedInquiry.phone}</p></div>
                                <div><p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Email</p><p className="font-bold text-slate-800 break-all">{selectedInquiry.email}</p></div>
                            </div>

                            {/* Requirements List */}
                            <div>
                                <h3 className="font-black uppercase text-slate-700 mb-4 text-sm flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                                    Job Requirements ({selectedInquiry.requirements?.length || 0})
                                </h3>
                                
                                {selectedInquiry.requirements && selectedInquiry.requirements.length > 0 ? (
                                    <div className="grid grid-cols-1 gap-4">
                                        {selectedInquiry.requirements.map((req, i) => (
                                            <div key={i} className="border border-slate-200 p-5 rounded-xl hover:shadow-md transition-shadow bg-white relative overflow-hidden group">
                                                <div className="absolute top-0 left-0 w-1 h-full bg-orange-400 group-hover:bg-orange-500 transition-colors"></div>
                                                
                                                <div className="flex justify-between items-start mb-3">
                                                    <div>
                                                        <h4 className="font-bold text-lg text-slate-800">
                                                            {req.role} 
                                                            {req.custom_role && <span className="text-slate-500 font-medium text-sm ml-2">({req.custom_role})</span>}
                                                        </h4>
                                                    </div>
                                                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-lg text-xs font-bold shadow-sm border border-green-200">
                                                        {req.vacancy_count} Vacancy
                                                    </span>
                                                </div>

                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6 text-sm text-slate-600 mb-3">
                                                    <div><span className="text-slate-400 text-xs uppercase font-bold block">Salary</span> <span className="font-semibold">{req.salary}</span></div>
                                                    {req.level && <div><span className="text-slate-400 text-xs uppercase font-bold block">Level</span> <span className="font-semibold">{req.level}</span></div>}
                                                    {req.subject && <div><span className="text-slate-400 text-xs uppercase font-bold block">Subject</span> <span className="font-semibold">{req.subject}</span></div>}
                                                </div>

                                                {req.qualifications && (
                                                    <div className="bg-slate-50 p-3 rounded-lg mt-2">
                                                        <p className="text-xs text-slate-400 font-bold uppercase mb-1">Qualifications / Notes</p>
                                                        <p className="text-sm text-slate-600 italic">"{req.qualifications}"</p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-slate-400 italic">No specific requirements listed.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SchoolInquiriesTab;